import {
  PlusOutlined,
  SearchOutlined,
  UploadOutlined
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputRef,
  message,
  Modal,
  notification,
  Popconfirm,
  Select,
  Space,
  Table,
} from "antd";
import type { ColumnsType, ColumnType } from "antd/lib/table";
import type { FilterConfirmProps } from "antd/lib/table/interface";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { CURRENT_CLUB_ID } from "../../_helpers";
import { ranksApi, teamsApi, UserBySSNResponse, userClubsApi, usersApi } from "../../_helpers/apiService";
import { PageTitle, SSNToggle } from "../../components";

interface DataType {
  key: number;
  id: number;
  last_name: string;
  first_name: string;
  email: string;
  phone_number: string;
  ssn: string;
  birth_date: string;
  gender: string;
  role: string;
  is_handicapped: boolean;
  is_old: boolean;
  affiliation?: string;
  profile_image_url?: string;
  // Club membership fields
  rank_id?: number;
  rank_date?: string;
  rate_id?: number;
  rate_date?: string;
  team_id?: number;
  is_team_leader: boolean;
}

const formItemLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};

const { Option } = Select;

// Filter state type
interface FilterState {
  first_name?: string;
  last_name?: string;
  email?: string;
  ssn?: string;
  phone_number?: string;
  rank_id?: number;
}

function Archers() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [data, setData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [ranks, setRanks] = useState<any>({});
  const [teams, setTeams] = useState<any>({});
  const userData = useSelector((state: any) => state.authentication.userData);

  // Column filter state
  const [filters, setFilters] = useState<FilterState>({});
  const searchInput = useRef<InputRef>(null);

  // State for existing user lookup
  const [existingUser, setExistingUser] = useState<UserBySSNResponse | null>(null);
  const [ssnSearchLoading, setSSNSearchLoading] = useState(false);
  const [ssnSearched, setSSNSearched] = useState(false);

  // Parse Mongolian SSN to extract birth date and gender
  // Format: XX YY MM DD NN (e.g., ЖЮ00220619 = 2000-02-06, male)
  const parseSSN = (ssn: string): { birthDate: string | null; gender: 'male' | 'female' | null } => {
    if (!ssn || ssn.length < 10) {
      return { birthDate: null, gender: null };
    }

    // Extract components (assuming format: 2 letters + 8 digits)
    const yearPart = ssn.substring(2, 4);
    const monthPart = ssn.substring(4, 6);
    const dayPart = ssn.substring(6, 8);
    const sequencePart = ssn.substring(8, 10);

    // Parse year, month, day
    const year = parseInt(yearPart, 10);
    const month = parseInt(monthPart, 10);
    const day = parseInt(dayPart, 10);
    const sequence = parseInt(sequencePart, 10);

    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(sequence)) {
      return { birthDate: null, gender: null };
    }

    // Determine century: if year > 30, assume 1900s, else 2000s
    const currentYear = new Date().getFullYear() % 100;
    const fullYear = year > currentYear ? 1900 + year : 2000 + year;

    // Validate date
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return { birthDate: null, gender: null };
    }

    // Format birth date
    const birthDate = `${fullYear}-${monthPart}-${dayPart}`;

    // Determine gender: odd sequence = male, even = female
    const gender = sequence % 2 === 1 ? 'male' : 'female';

    return { birthDate, gender };
  };

  // Handle SSN input change to auto-fill birth date and gender
  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const ssn = e.target.value;
    const { birthDate, gender } = parseSSN(ssn);

    if (birthDate && gender) {
      form.setFieldsValue({
        birthDate: dayjs(birthDate),
        gender: gender,
      });
    }
  };

  const deleteArcher = async (user: DataType) => {
    try {
      await usersApi.delete(user.id);
      message.success(
        `"${user.last_name} ${user.first_name}" харваач амжилттай устгалаа.`
      );
      // Refresh data
      fetchArchers();
    } catch (error: any) {
      console.error("Delete error:", error);
      message.error("Харваач устгахад алдаа гарлаа");
    }
  };

  const fetchArchers = async (page = currentPage, size = pageSize, currentFilters = filters) => {
    setLoading(true);
    try {
      const skip = (page - 1) * size;

      // Build filter params
      const filterParams: any = {
        skip,
        limit: size,
        role: 'archer' // Always filter for archers only
      };

      // Add active filters
      if (currentFilters.first_name) {
        filterParams.first_name = currentFilters.first_name;
      }
      if (currentFilters.last_name) {
        filterParams.last_name = currentFilters.last_name;
      }
      if (currentFilters.email) {
        filterParams.email = currentFilters.email;
      }
      if (currentFilters.ssn) {
        filterParams.ssn = currentFilters.ssn;
      }
      if (currentFilters.phone_number) {
        filterParams.phone_number = currentFilters.phone_number;
      }
      if (currentFilters.rank_id) {
        filterParams.rank_id = currentFilters.rank_id;
      }

      let archers: any[] = [];
      let totalCount = 0;

      // SuperAdmin sees all archers, Admin sees only club members
      if (userData.role === 'superadmin') {
        // For superAdmin: fetch all archers without club_id filter
        const response = await userClubsApi.getWithDetails('', filterParams);

        archers = response.data.items.map((user: any) => ({
          key: user.user_id,
          id: user.user_id,
          ...user
        }));
        totalCount = response.data.total;
      } else {
        // For admin: fetch only archers in the current club
        const response = await userClubsApi.getWithDetails(CURRENT_CLUB_ID, filterParams);

        archers = response.data.items.map((user: any) => ({
          key: user.user_id,
          id: user.user_id,
          ...user
        }));
        totalCount = response.data.total;
      }

      // Sort by first name
      archers.sort((a: any, b: any) =>
        a.first_name?.localeCompare(b.first_name)
      );

      setData(archers);
      setTotal(totalCount);
    } catch (error: any) {
      console.error("Fetch archers error:", error);
      message.error("Харваачдын мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRanks();
    fetchTeams();
  }, []);

  useEffect(() => {
    fetchArchers(currentPage, pageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filters]);

  const fetchRanks = async () => {
    try {
      const response = await ranksApi.getAll({
        club_id: CURRENT_CLUB_ID,
        limit: 1000
      });

      // Convert to object for easier lookup
      const ranksObj: any = {};
      response.data.items.forEach((rank: any) => {
        ranksObj[rank.id] = rank;
      });

      setRanks(ranksObj);
    } catch (error: any) {
      console.error("Fetch ranks error:", error);
      message.error("Цолын мэдээлэл татахад алдаа гарлаа");
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await teamsApi.getAll({
        club_id: CURRENT_CLUB_ID,
        limit: 1000
      });

      // Convert to object for easier lookup
      const teamsObj: any = {};
      response.data.items.forEach((team: any) => {
        teamsObj[team.id] = team;
      });

      setTeams(teamsObj);
    } catch (error: any) {
      console.error("Fetch teams error:", error);
      message.error("Багийн мэдээлэл татахад алдаа гарлаа");
    }
  };

  // Handle column filter search
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: keyof FilterState
  ) => {
    confirm();
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      [dataIndex]: selectedKeys[0] || undefined,
    }));
  };

  // Handle column filter reset
  const handleReset = (
    clearFilters: () => void,
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: keyof FilterState
  ) => {
    clearFilters();
    confirm();
    setCurrentPage(1);
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
  };

  // Get column search props for text input filter
  const getColumnSearchProps = (dataIndex: keyof FilterState, placeholder: string): ColumnType<DataType> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Хайх
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Цэвэрлэх
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    filteredValue: filters[dataIndex] ? [filters[dataIndex] as string] : null,
  });

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
    setExistingUser(null);
    setSSNSearched(false);
  };

  // Lookup user by SSN (registration number)
  const handleSSNLookup = async () => {
    const ssn = form.getFieldValue('ssn');
    if (!ssn || ssn.length < 2) {
      message.warning("Регистрийн дугаар бүрэн оруулна уу!");
      return;
    }

    setSSNSearchLoading(true);
    try {
      const response = await usersApi.getBySSN(ssn, CURRENT_CLUB_ID);
      setExistingUser(response.data);
      setSSNSearched(true);

      if (response.data.found) {
        if (response.data.is_member) {
          // User already exists in this club
          notification.warning({
            message: "Анхааруулга",
            description: `${response.data.user?.last_name} ${response.data.user?.first_name} энэ клубт аль хэдийн бүртгэлтэй байна.`,
          });
        } else {
          // User exists but not in this club - fill in the form
          const user = response.data.user!;
          form.setFieldsValue({
            email: user.email || '',
            phoneNumber: user.phone_number ? user.phone_number.replace('+976', '') : '',
            lastName: user.last_name,
            firstName: user.first_name,
            gender: user.gender || 'male',
            birthDate: user.birth_date ? dayjs(user.birth_date) : undefined,
            isHandicapped: user.is_handicapped,
            isOld: user.is_old,
            affiliation: user.affiliation || '',
          });

          notification.info({
            message: "Хэрэглэгч олдлоо",
            description: `${user.last_name} ${user.first_name} системд бүртгэлтэй байна. Энэ клубт нэмэхийн тулд үлдсэн талбаруудыг бөглөнө үү.`,
          });
        }
      } else {
        // User not found
        notification.info({
          message: "Хэрэглэгч олдсонгүй",
          description: "Шинэ хэрэглэгч бүртгэх боломжтой.",
        });
      }
    } catch (error: any) {
      console.error("SSN lookup error:", error);
      message.error("Хэрэглэгч хайхад алдаа гарлаа");
    } finally {
      setSSNSearchLoading(false);
    }
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        setConfirmLoading(true);
        try {
          let userId: number;

          // Check if we're adding an existing user to this club
          if (existingUser?.found && !existingUser.is_member) {
            // User exists but not in this club - just create club membership
            userId = existingUser.user!.id;
          } else {
            // Create new user
            const newUserData: any = {
              first_name: values.firstName,
              last_name: values.lastName,
              phone_number: values.phoneNumber.startsWith('+976')
                ? values.phoneNumber
                : '+976' + values.phoneNumber,
              ssn: values.ssn,
              gender: values.gender || 'male',
              password: values.phoneNumber.replace(/[^0-9]/g, ''), // Use phone as password
              role: 'archer',
              is_handicapped: values.isHandicapped || false,
              is_old: values.isOld || false,
              affiliation: values.affiliation || null,
            };

            // Only add email if provided and not empty
            if (values.email && values.email.trim() !== '') {
              newUserData.email = values.email.trim();
            }

            // Only add birth_date if provided
            if (values.birthDate) {
              newUserData.birth_date = dayjs(values.birthDate).format('YYYY-MM-DD');
            }

            // Create user
            const userResponse = await usersApi.create(newUserData);
            userId = userResponse.data.id;
          }

          // Create club membership
          await userClubsApi.create({
            club_id: CURRENT_CLUB_ID,
            user_id: userId,
            rank_id: values.rankId,
            rank_date: values.rateDate ? dayjs(values.rateDate).format('YYYY-MM-DD') : undefined,
            rate_id: values.rate2,
            rate_date: values.rateDate2 ? dayjs(values.rateDate2).format('YYYY-MM-DD') : undefined,
            team_id: values.teamId,
            is_team_leader: values.isTeamLeader || false,
          });

          notification.success({
            message: "Амжилттай",
            description: `${values.lastName} ${values.firstName} харваач амжилттай бүртгэгдлээ.`,
          });

          setVisible(false);
          setConfirmLoading(false);
          form.resetFields();
          setExistingUser(null);
          setSSNSearched(false);

          // Refresh archers list
          fetchArchers();

        } catch (error: any) {
          setConfirmLoading(false);
          console.error("Create archer error:", error);
          console.error("Error response:", error.response?.data);

          let errorMessage = "Харваач бүртгэхэд алдаа гарлаа";
          if (error.response?.data?.detail) {
            if (Array.isArray(error.response.data.detail)) {
              errorMessage = error.response.data.detail.map((err: any) =>
                `${err.loc?.join('.')}: ${err.msg}`
              ).join(', ');
            } else {
              errorMessage = error.response.data.detail;
            }
          }

          notification.error({
            message: "Амжилтгүй",
            description: errorMessage,
          });
        }
      })
      .catch((info) => {
        setConfirmLoading(false);
        console.log("Validate Failed:", info);
      });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "№",
      dataIndex: "id",
      key: "id",
      width: 60,
      fixed: 'left',
      render: (text, row, index) => (
        <>{(currentPage - 1) * pageSize + index + 1}</>
      ),
    },
    {
      title: "Овог",
      dataIndex: "last_name",
      key: "last_name",
      width: 120,
      ...getColumnSearchProps('last_name', 'Овог хайх'),
    },
    {
      title: "Нэр",
      dataIndex: "first_name",
      key: "first_name",
      width: 120,
      ...getColumnSearchProps('first_name', 'Нэр хайх'),
    },
    {
      title: "РД",
      dataIndex: "ssn",
      key: "ssn",
      width: 160,
      ...getColumnSearchProps('ssn', 'РД хайх'),
      render: (ssn, record) => <SSNToggle userId={record.id} />,
    },
    {
      title: "Улсын цол",
      dataIndex: "rank_id",
      key: "rank_id",
      width: 150,
      filters: Object.values(ranks)
        .filter((rank: any) => !rank.is_rank)
        .sort((a: any, b: any) => a.order - b.order)
        .map((rank: any) => ({ text: rank.name, value: rank.id })),
      filteredValue: filters.rank_id ? [filters.rank_id] : null,
      onFilter: () => true, // Server-side filtering
      render: (value) => {
        const currentRank = ranks[value];
        return currentRank ? currentRank.name : <span>-</span>;
      },
    },
    {
      title: "Цол огноо",
      dataIndex: "rank_date",
      key: "rank_date",
      width: 120,
      render: (value) => {
        if (!value) return "-";
        return dayjs(value).format("YYYY-MM-DD");
      },
    },
    {
      title: "Спортын зэрэг",
      dataIndex: "rate_id",
      key: "rate_id",
      width: 150,
      render: (value) => {
        const currentRank = ranks[value];
        return currentRank ? currentRank.name : <span>-</span>;
      },
    },
    {
      title: "Зэрэг огноо",
      dataIndex: "rate_date",
      key: "rate_date",
      width: 120,
      render: (value) => {
        if (!value) return "-";
        return dayjs(value).format("YYYY-MM-DD");
      },
    },
    {
      title: "Баг",
      dataIndex: "team_id",
      key: "team_id",
      width: 130,
      render: (value) => {
        const currentTeam = teams[value];
        return currentTeam ? currentTeam.name : <span>-</span>;
      },
    },
    {
      title: "Имэйл",
      dataIndex: "email",
      key: "email",
      width: 180,
      ...getColumnSearchProps('email', 'Имэйл хайх'),
    },
    {
      title: "Утас",
      dataIndex: "phone_number",
      key: "phone_number",
      width: 120,
      ...getColumnSearchProps('phone_number', 'Утас хайх'),
      render: (phoneNumber) => {
        // Remove +976 prefix if it exists
        if (phoneNumber && phoneNumber.startsWith('+976')) {
          return phoneNumber.substring(4);
        }
        return phoneNumber;
      },
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Link to={`/archers/${record.key}`} key={record.key}>
            Засах
          </Link>
          <Popconfirm
            title="Устгахдаа итгэлтэй байна уу?"
            onConfirm={() => deleteArcher(record)}
            okText="Тийм"
            cancelText="Үгүй"
          >
            <Button type="link" danger size="small">Устгах</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title="Харваачид"
        description={userData.role === "superadmin" ? "Бүх харваачдын жагсаалт" : "Харваачдын жагсаалт"}
        right={
          (userData.role !== "admin" && userData.role !== "superadmin") ? (
            <> </>
          ) : (
            <Space>
              <Button
                shape="round"
                onClick={() => navigate('/archers/import')}
                icon={<UploadOutlined />}
                size="large"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Excel импорт хийх
              </Button>
              <Button
                shape="round"
                onClick={() => setVisible(true)}
                icon={<PlusOutlined />}
                size="large"
                type="primary"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Харваач нэмэх
              </Button>
            </Space>
          )
        }
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  {/* Show active filters and clear button */}
                  {Object.keys(filters).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Space wrap>
                        <span style={{ color: '#666' }}>Идэвхтэй шүүлтүүр:</span>
                        {filters.first_name && (
                          <Alert
                            type="info"
                            message={`Нэр: ${filters.first_name}`}
                            closable
                            onClose={() => setFilters(prev => { const f = {...prev}; delete f.first_name; return f; })}
                            style={{ padding: '2px 8px' }}
                          />
                        )}
                        {filters.last_name && (
                          <Alert
                            type="info"
                            message={`Овог: ${filters.last_name}`}
                            closable
                            onClose={() => setFilters(prev => { const f = {...prev}; delete f.last_name; return f; })}
                            style={{ padding: '2px 8px' }}
                          />
                        )}
                        {filters.ssn && (
                          <Alert
                            type="info"
                            message={`РД: ${filters.ssn}`}
                            closable
                            onClose={() => setFilters(prev => { const f = {...prev}; delete f.ssn; return f; })}
                            style={{ padding: '2px 8px' }}
                          />
                        )}
                        {filters.email && (
                          <Alert
                            type="info"
                            message={`Имэйл: ${filters.email}`}
                            closable
                            onClose={() => setFilters(prev => { const f = {...prev}; delete f.email; return f; })}
                            style={{ padding: '2px 8px' }}
                          />
                        )}
                        {filters.phone_number && (
                          <Alert
                            type="info"
                            message={`Утас: ${filters.phone_number}`}
                            closable
                            onClose={() => setFilters(prev => { const f = {...prev}; delete f.phone_number; return f; })}
                            style={{ padding: '2px 8px' }}
                          />
                        )}
                        {filters.rank_id && (
                          <Alert
                            type="info"
                            message={`Цол: ${ranks[filters.rank_id]?.name || filters.rank_id}`}
                            closable
                            onClose={() => setFilters(prev => { const f = {...prev}; delete f.rank_id; return f; })}
                            style={{ padding: '2px 8px' }}
                          />
                        )}
                        <Button size="small" onClick={clearAllFilters}>
                          Бүгдийг цэвэрлэх
                        </Button>
                      </Space>
                    </div>
                  )}
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      sticky
                      scroll={{ x: 'max-content' }}
                      onChange={(_pagination, tableFilters) => {
                        // Handle rank_id filter from table dropdown
                        if (tableFilters.rank_id) {
                          const rankIds = tableFilters.rank_id as number[];
                          if (rankIds.length > 0) {
                            setFilters(prev => ({ ...prev, rank_id: rankIds[0] }));
                          } else {
                            setFilters(prev => { const f = {...prev}; delete f.rank_id; return f; });
                          }
                          setCurrentPage(1);
                        } else if (filters.rank_id && !tableFilters.rank_id) {
                          setFilters(prev => { const f = {...prev}; delete f.rank_id; return f; });
                          setCurrentPage(1);
                        }
                      }}
                      pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} / ${total}`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        onChange: (page, size) => {
                          setCurrentPage(page);
                          if (size && size !== pageSize) {
                            setPageSize(size);
                          }
                        },
                        onShowSizeChange: (_current, size) => {
                          setCurrentPage(1);
                          setPageSize(size);
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Харваач нэмэх"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Нэмэх"
        cancelText="Болих"
        width={720}
        confirmLoading={confirmLoading}
        okButtonProps={{ disabled: existingUser?.is_member }}
      >
        <Form
          {...formItemLayout}
          form={form}
          initialValues={{
            isHandicapped: false,
            isOld: false,
            isTeamLeader: false,
            gender: 'male',
          }}
        >
          {/* Phone number first */}
          <Form.Item
            label="Утасны дугаар"
            name="phoneNumber"
            rules={[{ required: true, message: "Утасны дугаар оруулна уу!" }]}
          >
            <Input
              placeholder="99001122"
              disabled={existingUser?.found && !existingUser.is_member}
            />
          </Form.Item>

          {/* SSN with search button - auto-fills birth date and gender */}
          <Form.Item
            label="Регистрийн дугаар"
            name="ssn"
            rules={[{ required: true, message: "Регистрийн дугаар оруулна уу!" }]}
          >
            <Input.Search
              placeholder="АА00112233"
              enterButton={
                <Button icon={<SearchOutlined />} loading={ssnSearchLoading}>
                  Хайх
                </Button>
              }
              onSearch={handleSSNLookup}
              onChange={handleSSNChange}
              disabled={existingUser?.found && !existingUser.is_member}
            />
          </Form.Item>

          {/* Alert for existing user in club */}
          {existingUser?.is_member && (
            <Form.Item wrapperCol={{ offset: 8, span: 14 }}>
              <Alert
                message="Энэ хэрэглэгч аль хэдийн бүртгэлтэй"
                description={`${existingUser.user?.last_name} ${existingUser.user?.first_name} энэ клубт аль хэдийн бүртгэлтэй байна.`}
                type="warning"
                showIcon
              />
            </Form.Item>
          )}

          {/* Info for existing user not in club */}
          {existingUser?.found && !existingUser.is_member && (
            <Form.Item wrapperCol={{ offset: 8, span: 14 }}>
              <Alert
                message="Хэрэглэгч олдлоо"
                description="Системд бүртгэлтэй хэрэглэгч. Зөвхөн клубын мэдээллийг бөглөнө үү."
                type="info"
                showIcon
              />
            </Form.Item>
          )}

          <Form.Item
            label="Имэйл хаяг"
            name="email"
            rules={[
              { required: false },
              { type: "email", message: "Зөв имэйл хаяг оруулна уу!" }
            ]}
          >
            <Input
              placeholder="example@gmail.com"
              disabled={existingUser?.found && !existingUser.is_member}
            />
          </Form.Item>
          <Form.Item
            label="Овог"
            name="lastName"
            rules={[{ required: true, message: "Овог оруулна уу!" }]}
          >
            <Input disabled={existingUser?.found && !existingUser.is_member} />
          </Form.Item>
          <Form.Item
            label="Нэр"
            name="firstName"
            rules={[{ required: true, message: "Нэр оруулна уу!" }]}
          >
            <Input disabled={existingUser?.found && !existingUser.is_member} />
          </Form.Item>
          {/* Gender and birth date are auto-calculated from SSN, hidden fields */}
          <Form.Item name="gender" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="birthDate" hidden>
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Хөгжлийн бэрхшээлтэй эсэх"
            name="isHandicapped"
            valuePropName="checked"
          >
            <Checkbox disabled={existingUser?.found && !existingUser.is_member} />
          </Form.Item>
          <Form.Item label="Ахмад харваач" name="isOld" valuePropName="checked">
            <Checkbox disabled={existingUser?.found && !existingUser.is_member} />
          </Form.Item>
          <Form.Item
            label="Харъяалал"
            name="affiliation"
            rules={[{ required: false }]}
          >
            <Input
              placeholder="Харъяалал (Жишээ: МУИС, МАК-23 гэх мэт)"
              disabled={existingUser?.found && !existingUser.is_member}
            />
          </Form.Item>
          <Form.Item
            label="Багийн ахлагч"
            name="isTeamLeader"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>

          {/* Club-specific fields - always editable */}
          <Form.Item
            label="Баг"
            name="teamId"
            rules={[{ required: true, message: "Баг сонгоно уу!" }]}
          >
            <Select placeholder="Баг" allowClear>
              {Object.values(teams)
                .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
                .map((team: any) => (
                  <Option key={team.id} value={team.id}>
                    {team.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Улсын цол"
            name="rankId"
            rules={[{ required: true, message: "Цол сонгоно уу!" }]}
          >
            <Select placeholder="Цол" allowClear>
              {Object.values(ranks)
                .filter((rank: any) => !rank.is_rank)
                .sort((a: any, b: any) =>
                  parseInt(a.order) > parseInt(b.order) ? 1 : -1
                )
                .map((rank: any) => (
                  <Option key={rank.id} value={rank.id}>
                    {rank.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Улсын цол авсан огноо"
            name="rateDate"
            rules={[{ required: true, message: "Цол авсан огноо сонгоно уу!" }]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Спортын зэрэг"
            name="rate2"
            rules={[{ required: false, message: "Цол сонгоно уу!" }]}
          >
            <Select placeholder="Спортын зэрэг" allowClear>
              {Object.values(ranks)
                .filter((rank: any) => rank.is_rank)
                .sort((a: any, b: any) =>
                  parseInt(a.order) > parseInt(b.order) ? 1 : -1
                )
                .map((rank: any) => (
                  <Option key={rank.id} value={rank.id}>
                    {rank.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Спортын зэрэг авсан огноо"
            name="rateDate2"
            rules={[
              {
                required: false,
                message: "Спортын зэрэг авсан огноо сонгоно уу!",
              },
            ]}
          >
            <DatePicker />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
export default Archers;
